import { logger } from "@/lib/logger";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Stethoscope, AlertTriangle, RefreshCw, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ConditionSummary {
  condition_name: string;
  icd_10_code: string | null;
  count: number;
  is_active_count: number;
}

export default function SiteConditionsDashboard() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();

  // Fetch medical conditions for this site
  const { data: conditionsData, isLoading, refetch } = useQuery({
    queryKey: ['site-conditions-details', siteId],
    queryFn: async () => {
      // Get all patients assigned to this site
      const { data: assignments, error: assignError } = await supabase
        .from('patient_trial_site_assignments')
        .select('profile_id')
        .eq('trial_site_id', siteId!);

      if (assignError) {
        logger.error('Error fetching assignments:', assignError);
        return { conditions: [], totalConditions: 0, uniqueConditions: 0, patientsWithConditions: 0 };
      }

      const profileIds = assignments?.map(a => a.profile_id) || [];
      
      if (profileIds.length === 0) {
        return { conditions: [], totalConditions: 0, uniqueConditions: 0, patientsWithConditions: 0 };
      }

      // Get medical conditions for these patients
      const { data: conditionRecords, error: conditionsError } = await supabase
        .from('medical_conditions')
        .select('condition_name, icd_10_code, is_primary, severity, profile_id')
        .in('profile_id', profileIds);

      if (conditionsError) {
        logger.warn('Error fetching conditions:', conditionsError);
        return { conditions: [], totalConditions: 0, uniqueConditions: 0, patientsWithConditions: 0 };
      }

      // Aggregate by condition name
      const conditionMap = new Map<string, { count: number; primaryCount: number; icdCode: string | null }>();
      const profilesWithConditions = new Set<string>();

      conditionRecords?.forEach(record => {
        if (record.condition_name) {
          profilesWithConditions.add(record.profile_id);
          const key = record.condition_name.toLowerCase().trim();
          const existing = conditionMap.get(key) || { count: 0, primaryCount: 0, icdCode: record.icd_10_code };
          conditionMap.set(key, {
            count: existing.count + 1,
            primaryCount: existing.primaryCount + (record.is_primary ? 1 : 0),
            icdCode: existing.icdCode || record.icd_10_code,
          });
        }
      });

      const conditions: ConditionSummary[] = Array.from(conditionMap.entries())
        .map(([name, data]) => ({
          condition_name: name.charAt(0).toUpperCase() + name.slice(1),
          icd_10_code: data.icdCode,
          count: data.count,
          is_active_count: data.primaryCount,
        }))
        .sort((a, b) => b.count - a.count);

      return {
        conditions,
        totalConditions: conditionRecords?.length || 0,
        uniqueConditions: conditions.length,
        patientsWithConditions: profilesWithConditions.size,
      };
    },
    enabled: !!siteId,
  });

  if (!siteId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <p className="text-center">Site ID is required</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title="Medical Conditions Dashboard"
        description="View medical conditions and ICD-10 codes for site patients"
        url={`/trial-site/${siteId}/conditions`}
      />

      <div className="container mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(`/trial-site/${siteId}/patient-management`)} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Patient Management
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-500" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Medical Conditions</h1>
                <p className="text-muted-foreground">
                  ICD-10 coded medical history for your patients
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-3xl font-bold">{conditionsData?.totalConditions || 0}</p>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Unique Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-bold text-blue-600">{conditionsData?.uniqueConditions || 0}</p>
                  <Stethoscope className="h-5 w-5 text-blue-500" />
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Patients with Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-3xl font-bold">{conditionsData?.patientsWithConditions || 0}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Conditions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Condition Distribution</CardTitle>
            <CardDescription>
              Aggregated medical conditions across all patients at this site
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Condition</TableHead>
                    <TableHead>ICD-10 Code</TableHead>
                    <TableHead className="text-right">Occurrences</TableHead>
                    <TableHead className="text-right">Primary</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conditionsData?.conditions.map((condition, index) => (
                    <TableRow key={`${condition.condition_name}-${index}`}>
                      <TableCell className="font-medium">
                        {condition.condition_name}
                      </TableCell>
                      <TableCell>
                        {condition.icd_10_code ? (
                          <Badge variant="outline" className="font-mono">
                            {condition.icd_10_code}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            No code
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {condition.count}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={condition.is_active_count > 0 ? "default" : "secondary"}>
                          {condition.is_active_count} primary
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!conditionsData?.conditions || conditionsData.conditions.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No medical conditions documented for this site
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
